import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Tv, AlertTriangle, ListVideo, LogOut } from "lucide-react";

export default function IPTVPlayer({ channel, isDriver, onOpenPanel, onExit }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !channel?.url) return;
    setError(false);
    const url = channel.url;
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    const looksHls = /\.m3u8(\?|$)/i.test(url) || url.toLowerCase().includes("m3u8");

    if (looksHls && Hls.isSupported()) {
      const hls = new Hls({ lowLatencyMode: true, liveSyncDurationCount: 3, enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) { try { hls.startLoad(); } catch (e) { setError(true); } }
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) { try { hls.recoverMediaError(); } catch (e) { setError(true); } }
        else { setError(true); }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // native HLS (iOS Safari)
      video.src = url;
      video.play().catch(() => {});
      video.onerror = () => setError(true);
    } else {
      video.src = url;
      video.play().catch(() => {});
      video.onerror = () => setError(true);
    }

    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [channel?.url]);

  return (
    <div className="w-full h-full bg-black relative flex items-center justify-center" data-testid="iptv-player">
      <video ref={videoRef} className="w-full h-full object-contain bg-black" controls playsInline autoPlay />

      <div className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-black/60 backdrop-blur rounded-full pl-1.5 pr-3 py-1.5 max-w-[70%]">
        {channel?.logo
          ? <img src={channel.logo} alt="" className="w-6 h-6 rounded object-contain bg-white/10 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
          : <Tv className="w-5 h-5 text-[#00D9FF] shrink-0" />}
        <span data-testid="iptv-current-channel" className="text-white text-sm font-semibold truncate">{channel?.name}</span>
        <span className="flex items-center gap-1 text-[10px] text-red-400 font-bold uppercase shrink-0"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Live</span>
      </div>

      {isDriver && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          <button data-testid="iptv-change-channel" onClick={onOpenPanel} className="flex items-center gap-1.5 bg-[#00D9FF] text-navy-ink text-xs font-semibold rounded-full px-3 py-1.5 hover:bg-[#33E4FF] transition-colors">
            <ListVideo className="w-3.5 h-3.5" /> Channels
          </button>
          <button data-testid="iptv-exit" onClick={onExit} title="Back to videos" className="flex items-center justify-center bg-black/60 text-[#A0A0AB] hover:text-white rounded-full w-8 h-8">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div data-testid="iptv-error" className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/90 text-center px-6">
          <AlertTriangle className="w-10 h-10 text-[#00D9FF]" />
          <p className="text-white font-semibold">This channel won't play in the browser.</p>
          <p className="text-[#A0A0AB] text-sm max-w-md">The stream may be offline or block browser playback (CORS/DRM). Try a different channel.</p>
          {isDriver && <button onClick={onOpenPanel} className="mt-1 h-10 px-5 bg-[#00D9FF] text-navy-ink rounded-full text-sm font-semibold">Pick another channel</button>}
        </div>
      )}
    </div>
  );
}
